import styled from "styled-components";
import moment from "moment-timezone";
import { RATypography } from "@/components/customs";
import { AirportLegInfo } from "@/components/molecules";
import { Row, Col, Modal } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { lightTheme } from "@/themes/ThemeConfig";
import { SvgInfoFill } from "@/assets/icons";
import { useSelector } from "react-redux";
import { selectFormatDateTimeFull } from "@/stores/slices/authSlice";
import { formatPhoneNumberIntl } from "react-phone-number-input";

export function ModalAirFreightDetails({ visible = false, onClose, booking = {} }) {
	const { code, created_at } = booking;
	const formatDTFull = useSelector(selectFormatDateTimeFull);

	const _renderGeneralInfo = () => {
		return (
			<WrapperFlightInfo>
				<CardInfo title="General Information" />
				<CardItem label="Company/Name" value={booking?.company_name ?? "_"} />
				<CardItem label="Contact name" value={booking?.contact?.name ?? "_"} />
				<CardItem label="Email" value={booking?.contact?.email ?? "_"} />
				<CardItem
					label="Phone"
					value={booking?.contact?.phone ? formatPhoneNumberIntl(booking?.contact?.phone) : "_"}
				/>
				<CardItem label="Additional Notes" value={booking?.note ?? "_"} />
			</WrapperFlightInfo>
		);
	};

	const _renderCargoInfo = () => {
		return (
			<WrapperFlightInfo>
				<CardInfo title="Cargo Information" />
				<CardItem label="Quantity" value={booking?.cargo_info?.quantity ?? "_"} />
				<CardItem label="Weight (lb)" value={booking?.cargo_info?.weight_lb ?? "_"} />
				<CardItem label="Height (inches)" value={booking?.cargo_info?.heigh_inch ?? "_"} />
				<CardItem label="Width (inches)" value={booking?.cargo_info?.width_inch ?? "_"} />
				<CardItem label="Length (inches)" value={booking?.cargo_info?.length_inch ?? "_"} />
			</WrapperFlightInfo>
		);
	};

	const _renderBookingInfo = () => {
		return (
			<WrapperBookingInfo>
				<Row gutter={[20, 20]}>
					<Col md={7} xs={10}>
						<RATypography variant="body4" color={lightTheme.color_text_300}>
							Request Code
						</RATypography>
						<RATypography variant="sub-heading5" color={lightTheme.color_text_500}>
							{code ?? "_"}
						</RATypography>
					</Col>
					<Col md={7} xs={14}>
						<RATypography variant="body4" color={lightTheme.color_text_300}>
							Created At
						</RATypography>
						<RATypography variant="sub-heading5" color={lightTheme.color_text_500}>
							{created_at ? moment(created_at).format(formatDTFull) : "_"}
						</RATypography>
					</Col>
				</Row>
			</WrapperBookingInfo>
		);
	};

	return (
		<StyledModal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => onClose(false)}
			footer={null}
			width="720px"
		>
			<ContainerBookingDetails data-testid="airFreightDetailsModal_content">
				<Row
					style={{
						position: "sticky",
						top: 0,
						zIndex: 1,
						backgroundColor: "#FFF",
						paddingBottom: "20px",
						paddingTop: "30px",
					}}
				>
					<Col md={24}>
						<WrapperContent>
							<RATypography variant="h3">Request Details</RATypography>
						</WrapperContent>
					</Col>
				</Row>
				{_renderBookingInfo()}
				<WrapperLegInfo className="ra-box-d">
					<AirportLegInfo from_airport={booking?.from_airport} to_airport={booking?.to_airport} />
				</WrapperLegInfo>
				{_renderGeneralInfo()}
				{_renderCargoInfo()}
			</ContainerBookingDetails>
		</StyledModal>
	);
}

const CardItem = ({ label = "", value = "" }) => (
	<Row align="top">
		<Col xl={10} lg={12} md={12} xs={23}>
			<RATypography variant="body3" color="#595959">
				{label}
			</RATypography>
		</Col>
		<Col xl={14} lg={12} md={12} xs={23}>
			<RATypography variant="sub-heading4" color="#222222">
				{value}
			</RATypography>
		</Col>
	</Row>
);

const CardInfo = ({ title }) => (
	<WrapperCardInfo>
		<IconContainer>
			<SvgInfoFill color="#FFB11A" />
		</IconContainer>
		<RATypography variant="label3">{title}</RATypography>
	</WrapperCardInfo>
);

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background-color: #ffffff;
		border-radius: 24px;
		box-shadow: none;
	}
	.ant-modal-close {
		width: 28px;
		height: 28px;
		top: 22px;
		right: 22px;
		@media (max-width: 576px) {
			top: 22px;
			right: 4px;
		}
	}
	.ant-modal-body {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 80vh;
	}
	@media (max-width: 576px) {
		padding: 0 12px;
	}
`;

const ContainerBookingDetails = styled.div`
	width: "100%";
	padding: 0 30px 30px 30px;
	@media (max-width: 576px) {
		padding: 0;
	}
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperBookingInfo = styled.div`
	padding: 10px 12px;
	gap: 32px;
	width: 100%;
	min-height: 62px;
	background: #f9f9f9;
	border-radius: 6px;
	margin-bottom: 12px;
`;

const WrapperFlightInfo = styled.div`
	margin-top: 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px;
	width: 100%;
	background: #fff;
	border: 1px solid #e9e8e8;
	border-radius: 12px;
`;
const WrapperLegInfo = styled.div`
	width: 100%;
	padding: 16px clamp(4px, 2vw, 20px);
	gap: 16px;
	min-height: 72px;
	border: 1px dashed #e9e8e8;
	border-radius: 10px;
`;
const IconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px;
	background-color: #f3f2f2;
	border-radius: 50%;
`;
const WrapperCardInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 4px;
	@media (max-width: 767px) {
		margin-bottom: 16px;
	}
`;
